import { unref } from 'vue';

import i18n from '@/config/i18n';

import {
  capitalize,
} from '@/utils/stringHelpers';
import chunkArray from '@/utils/chunkArray';
import getPropertyData from '@/utils/getPropertyData';

import useMainState from '@/state/useMainState';
import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';

const {
  isOverview,
} = useMainState();
const {
  isStrictlyDMOUser,
} = useUserState();
const {
  sectionMetrics,
  intervals,
  sectionTitle,
  getSectionData,
} = useSectionState();
const {
  isStayDatesActive,
  geolocations,
  devices,
  travelParty,
  filteredOtas,
  hotelType,
  selectedMetric,
  source,
} = useFilterState();

const { t } = i18n.global;

const prepareExportData = async ({
  id,
  items,
  dates,
  isCtp,
} = {}) => {
  // map item data
  let itemsData = items
    .filter((i) => i.finished
      && (!id || id === i.id)) // filter by ID if provided
    .map(async (i) => {
      const itemData = {
        id: i.id,
        title: unref(i.title),
      };

      if (i.selectedInterval) {
        itemData.interval = (intervals
          .filter((intervalCfg) => intervalCfg.name === i.selectedInterval)[0] || {})
          .placeholder;
      }

      if (i.getImg && typeof i.getImg === 'function') {
        itemData.image = await i.getImg();
      }

      if (i.getExportData) {
        itemData.tableData = i.getExportData();
      }

      return itemData;
    });

  // extract all the needed export info
  itemsData = await Promise.all(itemsData);

  let propertyId = 0;
  let name;

  if (!isStrictlyDMOUser.value) {
    if (isOverview.value) {
      name = t('Overview of all properties');
    } else {
      const propertyData = getPropertyData();
      propertyId = propertyData.propertyId;
      name = propertyData.name;
    }
  } else {
    name = t('Overview of all destinations');
  }

  const { values = {} } = getSectionData(propertyId)?.geolocationVisits ?? {};
  const allGeolocations = values.all ?? [];

  const formattedGeolocations = geolocations.value
    .map((location) => {
      const match = allGeolocations.find(
        (c) => c.value === location || `${c.id}` === `${location}`,
      );

      return match?.name ?? match?.label ?? location;
    });

  // apply active filters only
  const filters = [
    {
      type: 'geolocations',
      title: t('Geolocations'),
      value: formattedGeolocations,
    },
    {
      type: 'devices',
      title: t('Devices'),
      value: devices.value,
    },
    {
      type: 'travelparty',
      title: t('Travel party'),
      value: travelParty.value,
    },
    {
      type: 'otas',
      title: 'OTAs',
      value: filteredOtas.value,
    },
    {
      type: 'hoteltype',
      title: t('Hotel Type'),
      value: hotelType.value,
    },
    {
      type: 'source',
      title: t('Source'),
      value: source.value,
    },
  ].filter((f) => f.value.length)
    .map((f) => ({
      ...f,
      // capitalize back to default and translate
      value: [...f.value.map((v) => t(capitalize(v)))],
    }));

  // stay dates
  if (isStayDatesActive.value) {
    filters.push({
      type: 'staydates',
      title: t('Stay Dates'),
      value: {
        startDate: dates.sdDateBegin,
        endDate: dates.sdDateEnd,
      },
    });
  }

  const metricName = (sectionMetrics.value
    .filter((m) => m.name === selectedMetric.value)[0] || {}).placeholder || '';
  const reportTitle = t('{reportTitle} benchmark', { reportTitle: sectionTitle.value });

  const exportData = {
    name: `${metricName} (${dates.startDate}_${dates.endDate})`,
    reportTitle,
    metricName: t(metricName),
    showCoverCtpDates: false,
    property: {
      id: isOverview.value ? null : propertyId,
      name,
    },
    dates: {
      startDate: dates.startDate,
      endDate: dates.endDate,
    },
    filters,
  };

  // split pages to contain only two charts
  const pages = chunkArray(itemsData, 2)
    .map((pageItems, idx) => {
      const pageData = {
        pageLayout: 'default',
        showVisitDates: true,
        showCtpDates: true,
        showFilters: false,
        items: pageItems,
      };

      if (idx === 0) {
        pageData.showFilters = true;
      }

      return pageData;
    });

  exportData.pages = pages;

  // compare to past
  if (isCtp) {
    exportData.dates.ctpDateBegin = dates.ctpDateBegin;
    exportData.dates.ctpDateEnd = dates.ctpDateEnd;
    exportData.showCoverCtpDates = true;
  }

  return exportData;
};

export default prepareExportData;
